import { useMutation } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'

import { useFeedback } from '@/components/feedback/feedback-context'
import { Modal } from '@/components/overlays/modal'
import { Button } from '@/components/primitives/button'

import {
  resetAdminAccountPassword,
  type AdminAccount,
  type AdminAccountPasswordResetResult,
} from './api'

interface AdminAccountResetPasswordModalProps {
  admin: AdminAccount
  onClose: () => void
  onReset: (result: AdminAccountPasswordResetResult & { email: string }) => void
  open: boolean
}

export function AdminAccountResetPasswordModal({
  admin,
  onClose,
  onReset,
  open,
}: AdminAccountResetPasswordModalProps) {
  const { t } = useTranslation('adminSettings')
  const { showFeedback } = useFeedback()

  const resetPasswordMutation = useMutation({
    mutationFn: () => resetAdminAccountPassword({ adminId: admin.id }),
    onError: () => {
      showFeedback({
        message: t('errors.submitFailed'),
        tone: 'error',
      })
    },
    onSuccess: (result) => {
      showFeedback({
        message: t('success.resetPassword'),
        tone: 'success',
      })
      onReset({ ...result, email: admin.email })
      onClose()
    },
  })

  return (
    <Modal
      closeLabel={t('adminAccounts.resetPasswordModal.close')}
      footer={
        <>
          <Button
            disabled={resetPasswordMutation.isPending}
            onClick={onClose}
            size="sm"
            variant="secondary"
          >
            {t('actions.cancel')}
          </Button>
          <Button
            form="admin-account-reset-password-form"
            isLoading={resetPasswordMutation.isPending}
            size="sm"
            type="submit"
          >
            {t('adminAccounts.resetPasswordModal.confirm')}
          </Button>
        </>
      }
      onClose={onClose}
      open={open}
      title={t('adminAccounts.resetPasswordModal.title')}
    >
      <form
        className="grid gap-3"
        id="admin-account-reset-password-form"
        onSubmit={(event) => {
          event.preventDefault()
          resetPasswordMutation.mutate()
        }}
      >
        <p className="text-sm leading-6 text-[var(--color-text)]">
          {t('adminAccounts.resetPasswordModal.description', { email: admin.email })}
        </p>
        <p className="text-sm leading-6 text-[var(--color-text-muted)]">
          {t('adminAccounts.resetPasswordModal.impact')}
        </p>
      </form>
    </Modal>
  )
}
